import React, { useState, useEffect, useRef, useMemo, useCallback, useDeferredValue } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragOverlay,
  type DragEndEvent,
  type DragStartEvent,
  type DragOverEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import './List.css'
import './ListInlineAdd.css'
import { SyncStatusIndicator } from '../components/SyncStatusIndicator'
import { ItemInputWithSuggestions } from '../components/ItemInputWithSuggestions'
import { AccessDeniedBanner } from '../components/AccessDeniedBanner'
import { useCommand } from '../hooks/useCommand'
import { useList } from '../hooks/useList'
import { useScenario } from '../hooks/useScenario'
import { useScenarioItems } from '../hooks/useScenarioItems'
import { useFlows } from '../hooks/useFlows'
import { getNextOrder } from '../../core/utils/ordering'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { PageHeader } from '../components/PageHeader'
import { PwaUpdateBanner } from '../components/PwaUpdateBanner'
import type { FlowRanking, FlowPreferenceStatus, Item } from '../../core/domain/models'
import { resolveFlowPreferenceStatus } from '../../core/domain/flowPreferenceStatus'
import { computeListFlowSections, computeDropTargetGhosts, type OrderedFlowItem } from './listFlowOrdering'
import { computeFlowRankingsNamePatch, computeFlowRankingsPatchForOrderedPosition, computeFlowRankingsPatchForPopupOrder } from './flowEditorMultiContainer'
import { FlowOrderPopup } from './FlowOrderPopup'
import { ListKindIcon } from '../components/PageKindIcons'
import { usePreventTouchScrollWhileDragging } from '../hooks/usePreventTouchScrollWhileDragging'
import { uxMetrics } from '../../core/utils/uxMetrics'
import { toastStore } from '../notifications/toast-store'
import { useSmartBack } from '../hooks/useSmartBack'
import { useVisualViewportBottomOffset } from '../hooks/useVisualViewportBottomOffset'
import {
  ChevronRightIcon,
  MoreVerticalIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from '../components/Icons'
import { DropdownMenu } from '../components/DropdownMenu'
import type { MenuItem } from '../components/DropdownMenu'
import { SortableItem, FlowModeUnavailableItem, ItemNameWithQuantity, noop } from './ListItem'
import { useListItemsState } from './useListItemsState'
import { computeFlowDragOver } from './listFlowDragOver'
import { ListModals, type ListModalsRef } from './ListModals'
import { CompletedSection } from './ListCompletedSection'
import { FlowToolbar } from './ListFlowToolbar'



function List() {
  const { scenarioId, listId } = useParams<{ scenarioId: string; listId: string }>()
  const navigate = useNavigate()

  const addItemInputRef = useRef<HTMLInputElement>(null)
  const modalsRef = useRef<ListModalsRef>(null)
  const backToScenario = useSmartBack(scenarioId ? `/scenario/${scenarioId}` : '/')
  const visualViewportBottomOffsetPx = useVisualViewportBottomOffset()
  const [searchParams, setSearchParams] = useSearchParams()
  const command = useCommand()
  const { list, items, loading, accessDenied, dismissAccessDenied } = useList(scenarioId!, listId!)
  const { scenario } = useScenario(scenarioId || '', { passive: true })
  
  const { items: scenarioItems } = useScenarioItems(scenarioId, { passive: true })

  const { flows } = useFlows(scenarioId || '')
  
  // Persist selected flow in URL query param
  const selectedFlowId = searchParams.get('flow') || ''
  const setSelectedFlowId = (flowId: string) => {
    setSearchParams(prev => {
      if (flowId) {
        prev.set('flow', flowId)
      } else {
        prev.delete('flow')
      }
      return prev
    }, { replace: true })
  }

  // Defer flow data processing so the chip highlights immediately while
  // the heavy re-rendering (dnd-kit SortableContext + all item components)
  // runs at lower priority. This keeps the UI responsive during flow switches.
  const deferredFlowId = useDeferredValue(selectedFlowId)
  const isFlowSwitchPending = selectedFlowId !== deferredFlowId

  // Derive selected flow directly from the flows array (already reactive via useFlows).
  // This avoids a separate onStateChange subscription and eliminates the render
  // cascade that useFlow's async effect → setState caused.
  const flow = useMemo(() => {
    if (!deferredFlowId) return null
    return flows.find(f => f.id === deferredFlowId) ?? null
  }, [flows, deferredFlowId])

  // Track if auto-selection has already occurred
  const autoSelectionAttemptedRef = useRef(false)

  // Auto-select default flow when list opens (if no flow is already selected)
  // Only runs once on mount to avoid interfering with user's flow selection
  useEffect(() => {
    if (!autoSelectionAttemptedRef.current && !selectedFlowId && flows.length > 0 && scenario?.defaultFlowId) {
      autoSelectionAttemptedRef.current = true
      const defaultFlow = flows.find(f => f.id === scenario.defaultFlowId)
      if (defaultFlow) {
        setSelectedFlowId(defaultFlow.id)
      }
    }
  }, [flows, selectedFlowId, scenario, setSelectedFlowId])

  // Determine if user has edit permissions
  const canEdit = scenario?.role === 'owner' || scenario?.role === 'editor'

  const [optimisticFlowRankings, setOptimisticFlowRankings] = useState<Record<string, FlowRanking>>({})
  const lastSyncedFlowRankingsRef = useRef<Record<string, FlowRanking> | null>(null)
  const lastSyncedFlowIdRef = useRef<string>('')

  const [newItemName, setNewItemName] = useState('')
  const [addItemError, setAddItemError] = useState('')
  const [showMenu, setShowMenu] = useState(false)
  const [showUnavailable, setShowUnavailable] = useState(false)
  // --- Drag & Drop (dnd-kit) ---
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [dndContextKey, setDndContextKey] = useState(0)

  // On mobile browsers, native scrolling can cancel an active drag gesture.
  // Prevent default touch scrolling only while a drag is active.
  usePreventTouchScrollWhileDragging(!!activeDragId)

  // Local state for drag operations (optimistic UI)
  const [localFlowItems, setLocalFlowItems] = useState<string[]>([])
  
  const listScrollRef = useRef<HTMLDivElement | null>(null)
  const flowScrollRef = useRef<HTMLDivElement | null>(null)

  // --- Item state (consolidated hook) ---
  const {
    itemsState, setItemsState,
    activeItems, completedItems, activeItemsById,
    completingIds, highlightedItemId,
    lastAddedItemIdRef, pendingDeletesRef, pendingReordersRef,
    completeItemWithAnimation, restoreItemImmediate, cancelCompletionAnimation,
  } = useListItemsState({ serverItems: items, deferredFlowId, localFlowItems })

  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);
  const [renamingItemId, setRenamingItemId] = useState<string | null>(null);

  const effectiveExpandedItemId = activeDragId ? null : expandedItemId

  const [quantityDrafts, setQuantityDrafts] = useState<Record<string, number>>({})

  const MAX_QUANTITY = 999

  // Stable refs for callback closures
  const itemsStateRef = useRef(itemsState)
  itemsStateRef.current = itemsState
  const itemsRef = useRef(items)
  itemsRef.current = items
  const quantityDraftsRef = useRef(quantityDrafts)
  quantityDraftsRef.current = quantityDrafts
  const flowsRef = useRef(flows)
  flowsRef.current = flows
  const selectedFlowIdRef = useRef(selectedFlowId)
  selectedFlowIdRef.current = selectedFlowId
  const commandRef = useRef(command)
  commandRef.current = command
  const isFlowModeRef = useRef(false)
  const currentFlowRankingsRef = useRef<Record<string, FlowRanking>>({})

  const [dragScrollSpacerHeight, setDragScrollSpacerHeight] = useState(0)
  const dragAnchorRef = useRef<{
    scrollTopBefore: number
    anchorTopBefore: number
  } | null>(null)

  const dragStartActiveOrderSignatureRef = useRef<string | null>(null)
  const dragStartFlowOrderedSignatureRef = useRef<string | null>(null)

  // Signature for localFlowItems — used by state-during-render sync to avoid
  // calling setState on every render (which would cause an infinite loop).
  const localFlowItemsSignatureRef = useRef<string>('')

  // Flow order popup state (shown on drop when ghosts are at the target position)
  const [flowOrderPopup, setFlowOrderPopup] = useState<{
    droppedItem: OrderedFlowItem
    ghostItems: OrderedFlowItem[]
    prevRank: number | null
    nextRank: number | null
    newOrderedIds: string[]
    flowName: string
    prevNeighborName: string | null
    nextNeighborName: string | null
  } | null>(null)

  // History entry ID for the flow order popup (pushed in handleDragEnd, not
  // in FlowOrderPopup's effect, to avoid React 18 StrictMode double-mount
  // corrupting the history stack).
  const flowOrderPopupHistoryIdRef = useRef<string | null>(null)
  // Stable ref to the close handler so the popstate effect can call the
  // latest version without re-registering the listener on every render.
  const handleFlowOrderPopupCloseRef = useRef<() => void>(() => {})

  const ORDER_GAP = 100

  const handleToggleExpand = useCallback((nextId: string | null) => {
    setExpandedItemId(nextId)
    if (!nextId) return

    const item = itemsStateRef.current.find((i: any) => i.id === nextId) || itemsRef.current.find((i: any) => i.id === nextId)
    const qty = typeof (item as any)?.quantity === 'number' ? (item as any).quantity : 1
    const clamped = Math.min(MAX_QUANTITY, Math.max(1, qty))
    setQuantityDrafts(prev => ({ ...prev, [nextId]: clamped }))
  }, [])

  const handleUpdateQuantityDraft = useCallback((itemId: string, quantity: number) => {
    setQuantityDrafts(prev => ({ ...prev, [itemId]: Math.min(MAX_QUANTITY, Math.max(1, quantity)) }))
  }, [])

  const handleSubmitQuantity = useCallback(async (itemId: string, updateMetaItem: boolean) => {
    const rawQuantity = quantityDraftsRef.current[itemId]
    const quantity = rawQuantity == null ? null : Math.min(MAX_QUANTITY, Math.max(1, rawQuantity))
    if (quantity == null) return
    try {
      await commandRef.current.updateItemQuantity(
        scenarioId!,
        listId!,
        itemId,
        quantity,
        updateMetaItem
      )
      setExpandedItemId(null)
    } catch (error) {
      console.error('Error updating quantity:', error)
    }
  }, [scenarioId, listId])

  // Sync optimistic rankings with server state synchronously during render.
  if (!deferredFlowId) {
    if (lastSyncedFlowIdRef.current !== '') {
      lastSyncedFlowRankingsRef.current = null
      lastSyncedFlowIdRef.current = ''
      setOptimisticFlowRankings({})
    }
  } else if (flow) {
    const nextRankings = flow.rankings || {}
    if (lastSyncedFlowIdRef.current !== deferredFlowId || lastSyncedFlowRankingsRef.current !== nextRankings) {
      lastSyncedFlowRankingsRef.current = nextRankings
      lastSyncedFlowIdRef.current = deferredFlowId
      setOptimisticFlowRankings(nextRankings)
    }
  }

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 10 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 300, tolerance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const isFlowMode = !!deferredFlowId
  const currentFlowRankings: Record<string, FlowRanking> = useMemo(() => 
    isFlowMode ? (optimisticFlowRankings || flow?.rankings || {}) : {}, 
    [isFlowMode, optimisticFlowRankings, flow?.rankings]
  );

  // Sync refs that depend on derived values computed above
  isFlowModeRef.current = isFlowMode
  currentFlowRankingsRef.current = currentFlowRankings

  // Memoize flow sections and only recompute when we have valid ranking data
  // This prevents flicker when switching flows
  const flowSections = useMemo(() => {
    if (!isFlowMode) return null
    // Wait until we've synced the rankings for the current flow
    if (!flow || lastSyncedFlowIdRef.current !== deferredFlowId) return null
    return computeListFlowSections({ 
      items: activeItems as Item[], 
      rankings: currentFlowRankings,
      scenarioItems 
    })
  }, [isFlowMode, flow, deferredFlowId, activeItems, currentFlowRankings, scenarioItems])

  const SEPARATOR_ID = 'flow-separator'

  // Memoize item IDs for SortableContext (normal mode) to avoid
  // creating a new array reference on every render.
  const normalModeItemIds = useMemo(() => activeItems.map(i => i.id), [activeItems])

  // Sync flow sections to localFlowItems synchronously during render.
  // This follows the React "storing information from previous renders" pattern
  // to eliminate the extra render cycle that an effect would cause.
  // Skip during drag to avoid overwriting the user's in-progress reordering.
  if (flowSections && !activeDragId) {
    const fullFlowItems = [
      ...flowSections.unorderedIds,
      SEPARATOR_ID,
      ...flowSections.orderedIds,
    ]
    const newSignature = fullFlowItems.join('|')
    if (localFlowItemsSignatureRef.current !== newSignature) {
      localFlowItemsSignatureRef.current = newSignature
      setLocalFlowItems(fullFlowItems)
    }
  }
  
  const handleDragStart = (event: DragStartEvent) => {
    const activeId = event.active.id as string

    const activatorType = (() => {
      const ev = event.activatorEvent
      if (!ev) return 'unknown'
      // dnd-kit provides native events (MouseEvent/TouchEvent/KeyboardEvent)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const type = String((ev as any).type || '')
      if (type.startsWith('touch')) return 'touch'
      if (type.startsWith('mouse') || type.startsWith('pointer')) return 'mouse'
      if (type.startsWith('key')) return 'keyboard'
      return 'unknown'
    })()
    uxMetrics.increment('list_drag_start', { mode: isFlowMode ? 'flow' : 'normal', input: activatorType })

    // Clear stale scroll ref to prevent delayed scroll to a previously added item (#8)
    lastAddedItemIdRef.current = null

    // Collapse any open item menu while dragging.
    setExpandedItemId(null)

    if (isFlowMode) {
      const separatorIndex = localFlowItems.indexOf(SEPARATOR_ID)
      const orderedIds = separatorIndex >= 0 ? localFlowItems.slice(separatorIndex + 1) : []
      dragStartFlowOrderedSignatureRef.current = JSON.stringify(orderedIds)
    } else {
      dragStartActiveOrderSignatureRef.current = JSON.stringify(activeItems.map(i => i.id))
    }

    setActiveDragId(activeId);
  };

  const resetDnd = () => {
    setActiveDragId(null)
    setDragScrollSpacerHeight(0)
    dragAnchorRef.current = null
    dragStartActiveOrderSignatureRef.current = null
    dragStartFlowOrderedSignatureRef.current = null
    // In rare cases (notably swipe-to-delete interactions), dnd-kit can be left
    // in a state where an item keeps the placeholder styling. Remounting the
    // DndContext reliably clears that state.
    setDndContextKey(k => k + 1)
  }

  const handleDragCancel = () => {
    resetDnd()
  }

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event
    if (!over) return

    const activeId = active.id as string
    const overId = over.id as string

    if (activeId !== overId) {
       // Check if this is Flow Mode
       if (isFlowMode) {
           setLocalFlowItems((items) => computeFlowDragOver(items, activeId, overId, SEPARATOR_ID));
       }
    }
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    setActiveDragId(null)
    setDragScrollSpacerHeight(0)
    dragAnchorRef.current = null

    const dragStartActiveOrderSignature = dragStartActiveOrderSignatureRef.current
    const dragStartFlowOrderedSignature = dragStartFlowOrderedSignatureRef.current
    dragStartActiveOrderSignatureRef.current = null
    dragStartFlowOrderedSignatureRef.current = null

    // Dropped outside a valid target: treat as no-op.
    if (!over) return

    if (isFlowMode && flowSections) { 
      // Calculate final position in the unified list
      const separatorIndex = localFlowItems.indexOf(SEPARATOR_ID);
      const activeIndex = localFlowItems.indexOf(active.id as string);
      
      if (activeIndex === -1) return; 
      
      const isOrdered = activeIndex > separatorIndex;
      
      // Calculate new ordered items list based on everything AFTER the separator
      const newOrderedIds = localFlowItems.slice(separatorIndex + 1);

      // If the ordered list didn't change, don't send a command.
      const newOrderedSignature = JSON.stringify(newOrderedIds)
      if (dragStartFlowOrderedSignature && dragStartFlowOrderedSignature === newOrderedSignature) {
        return
      }
      
      // If the item is now in the Ordered section:
      if (isOrdered) {
          const activeId = active.id as string
          const activeItem = activeItemsById.get(activeId)

          // Check for ghost items at the drop position
          const dropGhosts = computeDropTargetGhosts({
            orderedWithGhosts: flowSections.orderedWithGhosts,
            rankings: currentFlowRankings,
            droppedItemId: activeId,
            newOrderedRealIds: newOrderedIds,
          })

          if (dropGhosts.ghostItems.length > 0) {
            // Show popup for precise ordering among ghosts
            // Apply the dropped item's ranking immediately (user sees it take effect)
            const initialPatch = computeFlowRankingsPatchForPopupOrder({
              popupItemIds: [activeId, ...dropGhosts.ghostItems.map(g => g.id)],
              prevRank: dropGhosts.prevRank,
              nextRank: dropGhosts.nextRank,
              itemNames: { [activeId]: activeItem?.name ?? activeId },
              existingRankings: currentFlowRankings,
            })

            const previousRankings = { ...currentFlowRankings }
            setOptimisticFlowRankings(prev => ({ ...prev, ...initialPatch }))
            command.updateFlowRankings(scenarioId!, selectedFlowId!, initialPatch).catch(e => {
              console.error(e)
              // Functional rollback: only revert patched keys, preserving other server updates (#7)
              setOptimisticFlowRankings(prev => {
                const reverted = { ...prev }
                for (const key of Object.keys(initialPatch)) {
                  if (previousRankings[key]) {
                    reverted[key] = previousRankings[key]
                  } else {
                    delete reverted[key]
                  }
                }
                return reverted
              })
            })

            // Look up neighbor names
            const prevNeighborName = dropGhosts.prevRealId
              ? (activeItemsById.get(dropGhosts.prevRealId)?.name ?? null)
              : null
            const nextNeighborName = dropGhosts.nextRealId
              ? (activeItemsById.get(dropGhosts.nextRealId)?.name ?? null)
              : null

            setFlowOrderPopup({
              droppedItem: { id: activeId, name: activeItem?.name ?? activeId, isGhost: false },
              ghostItems: dropGhosts.ghostItems,
              prevRank: dropGhosts.prevRank,
              nextRank: dropGhosts.nextRank,
              newOrderedIds,
              flowName: flow?.name ?? '',
              prevNeighborName,
              nextNeighborName,
            })

            // Push a history entry so the browser Back button closes the popup
            // instead of navigating away from the list.  Done here in the event
            // handler (not in FlowOrderPopup's mount effect) so it executes
            // exactly once and is immune to React 18 StrictMode double-mount.
            const popupId = `${Date.now()}-${Math.random().toString(16).slice(2)}`
            flowOrderPopupHistoryIdRef.current = popupId
            const existingHistState = window.history.state
            window.history.pushState(
              existingHistState && typeof existingHistState === 'object'
                ? { ...existingHistState, __ordelioFlowOrderPopupId: popupId }
                : { __ordelioFlowOrderPopupId: popupId },
              ''
            )
            return
          }

          // No ghosts at drop position: apply immediately
          const patch = computeFlowRankingsPatchForOrderedPosition({
            rankings: currentFlowRankings,
            orderedIdsAfter: newOrderedIds,
            activeId,
            itemName: activeItem?.name,
            orderGap: ORDER_GAP,
          })
          
          if (patch) {
            const previousRankings = { ...currentFlowRankings }
            setOptimisticFlowRankings(prev => ({ ...prev, ...patch }));
            try {
                await command.updateFlowRankings(scenarioId!, selectedFlowId!, patch)
            } catch (e) {
                console.error(e);
                // Functional rollback: only revert patched keys, preserving other server updates (#7)
                setOptimisticFlowRankings(prev => {
                  const reverted = { ...prev }
                  for (const key of Object.keys(patch)) {
                    if (previousRankings[key]) {
                      reverted[key] = previousRankings[key]
                    } else {
                      delete reverted[key]
                    }
                  }
                  return reverted
                })
            }
          }
      }
      // Note: Items cannot be moved from Ordered back to Unordered (prevented in handleDragOver)
      return
    }

    if (over && active.id !== over.id) {
        // Calculate reorder
        const oldIndex = activeItems.findIndex(i => i.id === active.id);
        const newIndex = activeItems.findIndex(i => i.id === over.id);

        if (oldIndex !== -1 && newIndex !== -1) {
            const movedItem = activeItems[oldIndex];
            const previousItemsState = [...itemsState];

            // Optimistic update logic
            const newOrderArray = arrayMove(activeItems, oldIndex, newIndex);

            // If the final order is unchanged compared to drag start, don't send a command.
            const nextActiveOrderSignature = JSON.stringify(newOrderArray.map(i => i.id))
            if (dragStartActiveOrderSignature && dragStartActiveOrderSignature === nextActiveOrderSignature) {
              return
            }

            // Logic to calculate specific 'order' float value
            let prevOrder = 0;
            let nextOrder = 0;

            if (newIndex === 0) {
                const nextItem = newOrderArray[1]; // Item after the moved one
                nextOrder = nextItem ? (nextItem.order ?? 100) : 100;
                prevOrder = 0;
            } else if (newIndex >= newOrderArray.length - 1) {
                const prevItem = newOrderArray[newOrderArray.length - 2];
                prevOrder = prevItem ? (prevItem.order ?? (newOrderArray.length * 100)) : 0;
                nextOrder = prevOrder + 200;
            } else {
                const prevItem = newOrderArray[newIndex - 1]; // Item before
                const nextItem = newOrderArray[newIndex + 1]; // Item after
                prevOrder = prevItem ? (prevItem.order ?? 0) : 0;
                nextOrder = nextItem ? (nextItem.order ?? 0) : 0;
            }

            const newOrder = (prevOrder + nextOrder) / 2;

            const newOrderArrayWithUpdatedOrder = newOrderArray.map((i) =>
              i.id === active.id ? { ...i, order: newOrder } : i
            );

            // Track the pending reorder to prevent snap-back during server sync
            pendingReordersRef.current.set(active.id as string, newOrder);

            // Update local state immediately to prevent flicker
            // We reconstruct the full list by combining new active order + existing completed items
            // Note: This temporarily changes the internal array structure to be [active..., completed...]
            // but that's fine as usage separates them anyway.
            setItemsState([...newOrderArrayWithUpdatedOrder, ...completedItems]);

            try {
                await command.reorderItem(
                  scenarioId!,
                  listId!,
                  active.id as string,
                  newOrder,
                  movedItem?.name,
                  {
                    onUndo: () => {
                      uxMetrics.increment('list_undo_used', { view: 'List', scenarioId: scenarioId ?? 'unknown', listId: listId ?? 'unknown', action: 'reorderItem' })
                      pendingReordersRef.current.delete(active.id as string)
                      setItemsState(previousItemsState)
                    }
                  }
                );
                 // Clear pending reorder after successful server update
                 pendingReordersRef.current.delete(active.id as string);
            } catch (e) {
                console.error("Failed to reorder", e);
              uxMetrics.increment('list_command_error', { view: 'List', scenarioId: scenarioId ?? 'unknown', listId: listId ?? 'unknown', command: 'reorderItem' })
                // Clear pending reorder and revert on error
                pendingReordersRef.current.delete(active.id as string);
                setItemsState(items); // Revert to server state
            }
        }
    }
  };





  useEffect(() => {
    // Force reset of inline add input when switching lists
    setNewItemName('');
    setAddItemError('');
  }, [listId]);

  useEffect(() => {
    // If the list disappears (deleted), navigate back.
    // Use replace so the deleted list URL is removed from history,
    // preventing a back-button loop (deleted list → scenario → back → deleted list → ...).
    if (!loading && scenarioId && listId && !list) {
      navigate(`/scenario/${scenarioId}`, { replace: true })
    }
  }, [loading, scenarioId, listId, list])

  const addItem = async (value?: string) => {
    const itemName = (typeof value === 'string' && value) ? value : newItemName;
    if (!itemName?.trim()) return

    const baseTags = { view: 'List', scenarioId: scenarioId ?? 'unknown', listId: listId ?? 'unknown' }
    uxMetrics.increment('list_add_item_attempt', baseTags)
    const endAddItemTimer = uxMetrics.startTimer('list_add_item_latency_ms', baseTags)

    // Check for duplicate names (case-insensitive)
    const normalizedName = itemName.trim().toLowerCase();
    const isDuplicate = items.some(item =>
      item.name.toLowerCase() === normalizedName && !pendingDeletesRef.current.has(item.id)
    );
    
    if (isDuplicate) {
      setAddItemError(`An item named "${itemName.trim()}" already exists in this list.`);
      uxMetrics.increment('list_add_item_duplicate', baseTags)
      endAddItemTimer({ outcome: 'duplicate' })
      return;
    }

    try {
      // Calculate next order using centralized utility
      const nextOrder = getNextOrder(items)

      const trimmedName = itemName.trim()
      const normalizedScenarioItemName = trimmedName.toLowerCase()
      const existingScenarioItem = scenarioItems.find(
        s => String(s?.name || '').trim().toLowerCase() === normalizedScenarioItemName
      )

      // If this matches a scenario suggestion, apply its preferred quantity.
      const preferredQuantity = existingScenarioItem?.quantity && existingScenarioItem.quantity >= 1
        ? existingScenarioItem.quantity
        : 1

      // Rule: list item id must equal scenario item id.
      // If scenario item exists by key, reuse its id; else create new id.
      const itemId = existingScenarioItem?.id ?? crypto.randomUUID()

      // Track the new item to scroll to it (set before await so scroll effect can find it)
      lastAddedItemIdRef.current = itemId

      // Use command hook for event-driven architecture
      await command.createItem(
        scenarioId!,
        listId!,
        itemId,
        trimmedName,
        nextOrder,
        preferredQuantity
      )

      endAddItemTimer({ outcome: 'success' })

      // useCommand triggers reactive refresh; UI reads via useList.
      setNewItemName('')
      setAddItemError('')
    } catch (error) {
      console.error('Error creating item:', error)

      endAddItemTimer({ outcome: 'error' })
      uxMetrics.increment('list_command_error', { ...baseTags, command: 'createItem' })
      // Clear the scroll ref on error since the item wasn't created
      lastAddedItemIdRef.current = null
      setAddItemError('Failed to add item. Please try again.')
    }
  }

  const toggleItemComplete = useCallback(async (itemId: string, isCompleted: boolean, itemName: string) => {
    const nextIsCompleted = !isCompleted;

    // Animate completion: keep the item in the active list briefly so it can fade/slide out,
    // then flip `isCompleted` to move it into the completed section.
    if (nextIsCompleted) {
      completeItemWithAnimation(itemId);
    } else {
      restoreItemImmediate(itemId);
    }

    try {
      // Use command hook for event-driven architecture
      await commandRef.current.completeItem(
        scenarioId!,
        listId!,
        itemId,
        nextIsCompleted,
        itemName,
        {
          onUndo: () => {
            uxMetrics.increment('list_undo_used', { view: 'List', scenarioId: scenarioId ?? 'unknown', listId: listId ?? 'unknown', action: 'completeItem' })
            const revertToCompleted = isCompleted

            if (revertToCompleted) {
              completeItemWithAnimation(itemId)
            } else {
              restoreItemImmediate(itemId)
            }
          }
        }
      )

      // useCommand triggers reactive refresh; UI reads via useList.
    } catch (error) {
      console.error('Error updating item:', error)

      uxMetrics.increment('list_command_error', { view: 'List', scenarioId: scenarioId ?? 'unknown', listId: listId ?? 'unknown', command: 'completeItem' })

      cancelCompletionAnimation(itemId);
      // Targeted revert: only restore the affected item's state, preserving other changes (#3)
      setItemsState(prev => prev.map(i =>
        i.id === itemId ? { ...i, isCompleted: isCompleted } : i
      ));
    }
  }, [completeItemWithAnimation, restoreItemImmediate, cancelCompletionAnimation, scenarioId, listId])

  const deleteItem = useCallback(async (itemId: string, itemName: string) => {
    // Removed confirmation for smoother UX
    // if (!confirm('Are you sure you want to delete this item?')) return

    // Capture item before removal for error recovery (#5)
    const removedItem = itemsStateRef.current.find((i: any) => i.id === itemId);

    // Optimistically remove from UI immediately
    pendingDeletesRef.current.add(itemId);
    setItemsState(prev => prev.filter(i => i.id !== itemId));

    // If in flow mode, ensure the item has a name in the ranking so it becomes a ghost
    if (isFlowModeRef.current) {
      const patch = computeFlowRankingsNamePatch({ rankings: currentFlowRankingsRef.current, itemId, itemName })
      if (patch) {
        setOptimisticFlowRankings(prev => ({
          ...prev,
          ...patch,
        }))
      }
    }

    try {
      // Use command hook for event-driven architecture
      await commandRef.current.deleteItem(
        scenarioId!,
        listId!,
        itemId,
        itemName,
        {
          onUndo: () => {
            pendingDeletesRef.current.delete(itemId)
          }
        }
      )

      // useCommand triggers reactive refresh; UI reads via useList.
    } catch (error) {
      console.error('Error deleting item:', error);
      // If error, remove from pending deletes so it reappears
      pendingDeletesRef.current.delete(itemId);
      // Restore from captured snapshot instead of stale `items` closure (#5)
      if (removedItem) {
        setItemsState(prev => [...prev, removedItem]);
      }
    }
  }, [scenarioId, listId])

  const startRenameItem = useCallback((itemId: string) => {
    setRenamingItemId(itemId)
  }, [])

  const submitRenameItem = useCallback(async (itemId: string, newName: string, oldName: string) => {
    setRenamingItemId(null)
    if (newName === oldName) return

    try {
      await commandRef.current.renameItem(
        scenarioId!,
        listId!,
        itemId,
        newName,
        oldName
      )
    } catch (error) {
      console.error('Error renaming item:', error)
    }
  }, [scenarioId, listId])

  // Keep a ref to handleToggleFlowAvailability so the undo callback
  // always calls the latest version (avoids stale closures after unmount).
  const toggleFlowRef = useRef<(itemId: string, flowId: string, isAvailable: boolean) => void>(() => {})

  const handleToggleFlowAvailability = useCallback(async (itemId: string, flowId: string, isAvailable: boolean) => {
    const targetFlow = flowsRef.current.find((f: any) => f.id === flowId);
    if (!targetFlow) return;

    const currentRankings = targetFlow.rankings || {};
    const currentRanking = currentRankings[itemId];
    
    const patch = {
      [itemId]: {
        r: currentRanking?.r ?? 0,
        n: currentRanking?.n ?? itemsRef.current.find((i: any) => i.id === itemId)?.name,
        notAvailable: !isAvailable
      }
    };

    // Optimistic update for current flow
    if (flowId === selectedFlowIdRef.current) {
      setOptimisticFlowRankings(prev => ({ ...prev, ...patch }));
    }

    // When removing from the currently-viewed flow, the item vanishes.
    // Show a toast with Undo so the user can reverse it.
    if (!isAvailable && flowId === selectedFlowIdRef.current) {
      const itemName = currentRanking?.n ?? itemsRef.current.find((i: any) => i.id === itemId)?.name ?? 'Item';
      toastStore.push({
        message: `Removed from ${targetFlow.name}`,
        itemName,
        type: 'delete',
        duration: 5000,
        action: {
          label: 'Undo',
          onClick: () => {
            // Call through the ref so we always use the latest handler
            // (avoids stale flows/rankings from the original closure).
            toggleFlowRef.current(itemId, flowId, true);
          },
        },
      });
    }

    try {
      await commandRef.current.updateFlowRankings(scenarioId!, flowId, patch);
    } catch (e) {
      console.error('Error toggling flow availability:', e);
      // Revert optimistic update
      if (flowId === selectedFlowIdRef.current) {
        setOptimisticFlowRankings(prev => {
          const reverted = { ...prev };
          if (currentRanking) {
            reverted[itemId] = currentRanking;
          } else {
            delete reverted[itemId];
          }
          return reverted;
        });
      }
    }
  }, [scenarioId]);

  // Always keep the ref in sync with the latest handler.
  toggleFlowRef.current = handleToggleFlowAvailability;

  // Track items that are fading out (became unavailable on selected flow after collapse)
  const [fadingOutIds, setFadingOutIds] = useState<Set<string>>(new Set())

  const handleFlowPreferencesCommit = useCallback(async (itemId: string, changes: Record<string, FlowPreferenceStatus>) => {
    if (!scenarioId) return

    // Build the command payload
    const flowPreferences: Record<string, { status: 'normal' | 'preferred' | 'notAvailable' }> = {}
    let madeUnavailableOnSelectedFlow = false

    for (const [flowId, status] of Object.entries(changes)) {
      // Only send changes that differ from current state
      const currentFlow = flowsRef.current.find((f: any) => f.id === flowId)
      const currentStatus = resolveFlowPreferenceStatus(currentFlow?.rankings?.[itemId])
      if (status !== currentStatus) {
        flowPreferences[flowId] = { status }

        // Check if item will become unavailable on selected flow
        if (flowId === selectedFlowIdRef.current && status === 'notAvailable') {
          madeUnavailableOnSelectedFlow = true
        }
      }
    }

    if (Object.keys(flowPreferences).length === 0) return

    // Optimistic update: update all affected flows
    for (const [flowId, pref] of Object.entries(flowPreferences)) {
      if (flowId === selectedFlowIdRef.current) {
        setOptimisticFlowRankings(prev => {
          const existingRanking = prev[itemId] || flowsRef.current.find((f: any) => f.id === flowId)?.rankings?.[itemId]
          return {
            ...prev,
            [itemId]: {
              r: existingRanking?.r ?? 0,
              n: existingRanking?.n ?? itemsRef.current.find((i: any) => i.id === itemId)?.name,
              notAvailable: pref.status === 'notAvailable',
              status: pref.status
            }
          }
        })
      }
    }

    // Fade-out if made unavailable on selected flow
    if (madeUnavailableOnSelectedFlow) {
      setFadingOutIds(prev => new Set([...prev, itemId]))
      setTimeout(() => {
        setFadingOutIds(prev => {
          const next = new Set(prev)
          next.delete(itemId)
          return next
        })
      }, 500)
    }

    // Show toast with undo
    const itemName = itemsRef.current.find((i: any) => i.id === itemId)?.name ?? 'Item'
    const flowNames = Object.keys(flowPreferences)
      .map(fid => flowsRef.current.find((f: any) => f.id === fid)?.name)
      .filter(Boolean)

    // Capture previous status before the command updates server state
    const previousPreferences: Record<string, { status: 'normal' | 'preferred' | 'notAvailable' }> = {}
    for (const [flowId] of Object.entries(flowPreferences)) {
      const currentFlow = flowsRef.current.find((f: any) => f.id === flowId)
      const previousStatus = resolveFlowPreferenceStatus(currentFlow?.rankings?.[itemId])
      previousPreferences[flowId] = { status: previousStatus }
    }
    
    toastStore.push({
      message: `Updated preferences${flowNames.length === 1 ? ` for ${flowNames[0]}` : ''}`,
      itemName,
      type: madeUnavailableOnSelectedFlow ? 'delete' : 'success',
      duration: 5000,
      action: {
        label: 'Undo',
        onClick: () => {
          commandRef.current.setItemFlowPreferences(scenarioId!, itemId, previousPreferences, { skipNotification: true })
        },
      },
    })

    try {
      await commandRef.current.setItemFlowPreferences(scenarioId!, itemId, flowPreferences, { skipNotification: true })
    } catch (e) {
      console.error('Error setting item flow preferences:', e)
    }
  }, [scenarioId])

  const handleFlowOrderPopupReorder = async (orderedItemIds: string[]) => {
    if (!flowOrderPopup || !scenarioId || !selectedFlowId) return

    const ghostById = new Map(flowOrderPopup.ghostItems.map(g => [g.id, g]))
    const itemNames: Record<string, string> = {}
    for (const id of orderedItemIds) {
      const active = activeItemsById.get(id)
      if (active) {
        itemNames[id] = active.name
      } else {
        const ghost = ghostById.get(id)
        if (ghost) itemNames[id] = ghost.name
      }
    }

    const patch = computeFlowRankingsPatchForPopupOrder({
      popupItemIds: orderedItemIds,
      prevRank: flowOrderPopup.prevRank,
      nextRank: flowOrderPopup.nextRank,
      itemNames,
      existingRankings: currentFlowRankings,
    })

    const previousRankings = { ...currentFlowRankings }
    setOptimisticFlowRankings(prev => ({ ...prev, ...patch }))

    try {
      await command.updateFlowRankings(scenarioId, selectedFlowId, patch)
    } catch (e) {
      console.error(e)
      // Functional rollback: only revert patched keys, preserving other server updates (#7)
      setOptimisticFlowRankings(prev => {
        const reverted = { ...prev }
        for (const key of Object.keys(patch)) {
          if (previousRankings[key]) {
            reverted[key] = previousRankings[key]
          } else {
            delete reverted[key]
          }
        }
        return reverted
      })
    }
  }

  const handleFlowOrderPopupClose = () => {
    const popupId = flowOrderPopupHistoryIdRef.current
    flowOrderPopupHistoryIdRef.current = null

    // If we're still on the popup's history entry, navigate back to remove it.
    // When closed via browser Back button the browser already navigated back,
    // so the popup ID won't match and we correctly skip the extra back().
    if (popupId) {
      const currentState = window.history.state as any
      if (currentState?.__ordelioFlowOrderPopupId === popupId) {
        window.history.back()
      }
    }

    setFlowOrderPopup(null)
  }
  handleFlowOrderPopupCloseRef.current = handleFlowOrderPopupClose

  // --- Flow order popup: back-button interception + body scroll lock ---
  // The popstate listener lives here (not in FlowOrderPopup) so that the
  // cleanup never calls history.back(), which StrictMode would trigger
  // during its mount→cleanup→remount cycle and corrupt the history stack.
  useEffect(() => {
    if (!flowOrderPopup) return

    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const handlePopState = () => {
      handleFlowOrderPopupCloseRef.current()
    }

    window.addEventListener('popstate', handlePopState)
    return () => {
      window.removeEventListener('popstate', handlePopState)
      document.body.style.overflow = prevOverflow
    }
  }, [!!flowOrderPopup])




  if (loading) {
    return <LoadingSpinner message="Loading list..." fullPage />
  }

  if (!list) {
    return (
      <div className="app-container">
        <div className="error-message">
          <p>List not found</p>
          <button onClick={backToScenario} className="back-btn">
            Back to Scenario
          </button>
        </div>
      </div>
    )
  }


  const completedSection = (
    <CompletedSection
      completedItems={completedItems}
      effectiveExpandedItemId={effectiveExpandedItemId}
      activeDragId={activeDragId}
      onToggleExpand={(id) => setExpandedItemId(id)}
      onToggleComplete={toggleItemComplete}
      onDelete={deleteItem}
      onStartRename={startRenameItem}
      onSubmitRename={submitRenameItem}
      renamingItemId={renamingItemId}
    />
  );

  const menuItems: MenuItem[] = [
    {
      icon: <PlusIcon />,
      label: 'Add Existing Items',
      onClick: () => navigate(`/scenario/${scenarioId}/list/${listId}/add-items`)
    },
    {
      divider: true,
      icon: <PencilIcon />,
      label: 'Edit List',
      onClick: () => modalsRef.current?.openEditModal()
    },
    {
      divider: true,
      icon: <TrashIcon />,
      label: 'Delete List',
      onClick: () => modalsRef.current?.openDeleteModal(),
      variant: 'danger'
    }
  ];



  return (
    <div
      className="app-container list-view-container"
      style={{ ['--visual-viewport-bottom-offset' as any]: `${visualViewportBottomOffsetPx}px` }}
    >
      <PageHeader
        kind="List"
        kindIcon={<ListKindIcon />}
        tone="list"
        title={list.name}
        backLabel="Scenario"
        onBack={backToScenario}
        actions={
          <>
            <SyncStatusIndicator />
            <div className="menu-container">
              <button 
                onClick={() => setShowMenu(!showMenu)} 
                className="menu-btn" 
                title="List options"
                aria-label="List options"
              >
                <MoreVerticalIcon />
              </button>
              <DropdownMenu 
                isOpen={showMenu} 
                onClose={() => setShowMenu(false)}
                items={menuItems}
              />
            </div>
          </>
        }
      />

      <PwaUpdateBanner />

      <main className="app-main">
        <div className="list-content">
          {accessDenied && (
            <AccessDeniedBanner onDismiss={dismissAccessDenied} />
          )}
          
          {list.description && (
            <div className="list-description">
              <p>{list.description}</p>
            </div>
          )}

          <div className="items-section">
            <div className="items-list" ref={listScrollRef}>
              <FlowToolbar
                flows={flows}
                selectedFlowId={selectedFlowId}
                onSelectFlow={setSelectedFlowId}
                onOpenFlowsModal={() => modalsRef.current?.openFlowsModal()}
                scenarioId={scenarioId!}
                listId={listId!}
              />

              <div className={`active-items ${isFlowMode ? 'mode-flow' : ''} ${isFlowSwitchPending ? 'flow-switch-pending' : ''}`}>
                <DndContext
                  key={dndContextKey}
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDragEnd={handleDragEnd}
                  onDragCancel={handleDragCancel}
                >
                  {isFlowMode && flowSections ? (
                    <div className="list-flow-scroll-container" ref={flowScrollRef}>
                      {flowSections.unavailableIds.length > 0 && (
                        <section>
                          <h3 className="completed-header-heading">
                            <button
                              type="button"
                              className="completed-header"
                              onClick={() => setShowUnavailable(!showUnavailable)}
                              aria-expanded={showUnavailable}
                              aria-controls="unavailable-items-panel"
                            >
                              <span className={`chevron ${showUnavailable ? 'expanded' : ''}`} aria-hidden="true">
                                <ChevronRightIcon />
                              </span>
                              <span>Not available in this flow ({flowSections.unavailableIds.length})</span>
                            </button>
                          </h3>
                          <div id="unavailable-items-panel" hidden={!showUnavailable}>
                            {showUnavailable && (
                              <div className="list-flow-items">
                                {flowSections.unavailableIds.map((id) => {
                                  const item = activeItemsById.get(id)
                                  if (!item) return null
                                  return <FlowModeUnavailableItem key={id} item={item} />
                                })}
                              </div>
                            )}
                          </div>
                        </section>
                      )}


                      {/* Unified Flow List */}
                      <SortableContext items={localFlowItems} strategy={verticalListSortingStrategy}>
                        <div className="list-flow-items">
                           {localFlowItems.map((id, idx) => {
                             if (id === SEPARATOR_ID) {
                                 const separatorTitle = 'In flow order'
                                 const separatorHint = ''
                               return (
                                 <SortableItem
                                   key={id}
                                   id={id}
                                     item={{ id: SEPARATOR_ID, name: separatorTitle, hint: separatorHint, isCompleted: false, listId: listId! } as any}
                                   // Props to render it as separator
                                   isSeparator={true}
                                   completing={false}
                                   onToggle={noop}
                                   isExpanded={false}
                                   onToggleExpand={noop}
                                   onDelete={noop}
                                 />
                               )
                             }
                             
                             // Check if this is the first unordered item (before separator)
                             const separatorIdx = localFlowItems.indexOf(SEPARATOR_ID);
                             const isFirstUnordered = idx === 0 && separatorIdx > 0;
                             
                             const item = activeItemsById.get(id)
                             
                             if (!item) return null
                             
                             // Determine if unordered based on position relative to separator
                             const separatorIndex = localFlowItems.indexOf(SEPARATOR_ID);
                             const myIndex = localFlowItems.indexOf(id);
                             const isUnordered = myIndex < separatorIndex;

                             return (
                               <React.Fragment key={id}>
                                 {isFirstUnordered && (
                                   <h3 className="list-flow-section-title">Not in order yet</h3>
                                 )}
                                 <SortableItem
                                   id={id}
                                   item={item}
                                   completing={completingIds.has(id)}
                                   onToggle={toggleItemComplete}
                                   isExpanded={effectiveExpandedItemId === id}
                                   onToggleExpand={handleToggleExpand}
                                   onDelete={deleteItem}
                                   onStartRename={startRenameItem}
                                   onSubmitRename={submitRenameItem}
                                   isRenaming={renamingItemId === id}
                                   quantityDraft={quantityDrafts[id]}
                                   onUpdateQuantityDraft={handleUpdateQuantityDraft}
                                   onSubmitQuantity={handleSubmitQuantity}
                                   isUnordered={isUnordered}
                                   justAdded={highlightedItemId === id}
                                   flows={flows}
                                   selectedFlowId={selectedFlowId}
                                   onToggleFlowAvailability={handleToggleFlowAvailability}
                                   onFlowPreferencesCommit={handleFlowPreferencesCommit}
                                   isFadingOut={fadingOutIds.has(id)}
                                 />
                               </React.Fragment>
                             )
                           })}

                           {activeDragId && dragScrollSpacerHeight > 0 && (
                             <div
                               aria-hidden="true"
                               className="drag-scroll-spacer"
                               style={{ height: dragScrollSpacerHeight }}
                             />
                           )}
                        </div>
                      </SortableContext>
                      {completedSection}
                    </div>
                  ) : (
                    <div className="list-flow-scroll-container">
                      <section className="list-flow-section">
                        <div className="list-flow-items">
                          <SortableContext items={normalModeItemIds} strategy={verticalListSortingStrategy}>
                            {activeItems.map((item) => (
                              <SortableItem
                                key={item.id}
                                id={item.id}
                                item={item}
                                completing={completingIds.has(item.id)}
                                onToggle={toggleItemComplete}
                                isExpanded={effectiveExpandedItemId === item.id}
                                onToggleExpand={handleToggleExpand}
                                onDelete={deleteItem}
                                onStartRename={startRenameItem}
                                onSubmitRename={submitRenameItem}
                                isRenaming={renamingItemId === item.id}
                                quantityDraft={quantityDrafts[item.id]}
                                onUpdateQuantityDraft={handleUpdateQuantityDraft}
                                onSubmitQuantity={handleSubmitQuantity}
                                justAdded={highlightedItemId === item.id}
                                flows={flows}
                                selectedFlowId={selectedFlowId}
                                onToggleFlowAvailability={handleToggleFlowAvailability}
                                onFlowPreferencesCommit={handleFlowPreferencesCommit}
                                isFadingOut={fadingOutIds.has(item.id)}
                              />
                            ))}
                          </SortableContext>
                        </div>
                      </section>
                      {completedSection}
                    </div>
                  )}

                  <DragOverlay>
                    {activeDragId ? (() => {
                      const item = activeItemsById.get(activeDragId) || items.find(i => i.id === activeDragId);
                      if (!item) return null;
                      
                      const separatorIndex = localFlowItems.indexOf(SEPARATOR_ID);
                      const itemIndex = localFlowItems.indexOf(activeDragId);
                      const isUnordered = isFlowMode && separatorIndex !== -1 && itemIndex !== -1 && itemIndex < separatorIndex;
                      
                      const dragQuantity = typeof item.quantity === 'number' ? item.quantity : 1;
                      return (
                        <div className={`item-card dragging ${isUnordered ? 'list-flow-unordered' : ''}`} style={{ pointerEvents: 'none' }}>
                          <div className="item-checkbox">
                            <input type="checkbox" checked={!!item.isCompleted} readOnly />
                          </div>
                          <div className="item-name">
                            <ItemNameWithQuantity name={item.name} quantity={dragQuantity} />
                          </div>
                        </div>
                      );
                    })() : null}
                  </DragOverlay>
                </DndContext>
              </div>


              {/* Completed items moved inside list-flow-scroll-container */}

            </div>
          </div>
        </div>

        <ListModals
          ref={modalsRef}
          list={list}
          items={items}
          flows={flows}
          selectedFlowId={selectedFlowId}
          setSelectedFlowId={setSelectedFlowId}
          canEdit={canEdit}
          scenario={scenario}
          scenarioId={scenarioId!}
          listId={listId!}
          backToScenario={backToScenario}
          command={command}
        />

        {flowOrderPopup && (
          <FlowOrderPopup
            flowName={flowOrderPopup.flowName}
            droppedItem={flowOrderPopup.droppedItem}
            ghostItems={flowOrderPopup.ghostItems}
            prevNeighborName={flowOrderPopup.prevNeighborName}
            nextNeighborName={flowOrderPopup.nextNeighborName}
            onReorder={handleFlowOrderPopupReorder}
            onClose={handleFlowOrderPopupClose}
          />
        )}
      </main>

      {/* Sticky Footer - Add Item Input */}
      <div className="list-footer">
        <div className="list-footer-content">
          <div className="item-card add-item-row add-item-row--prominent" style={{ marginTop: 0 }}>
            <div
              className="item-checkbox placeholder"
              role="button"
              tabIndex={0}
              aria-label="Focus add item input"
              onMouseDown={(e) => {
                // Prevent the div from stealing focus on click
                e.preventDefault()
              }}
              onClick={() => addItemInputRef.current?.focus()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  addItemInputRef.current?.focus()
                }
              }}
            >
              <span className="plus-icon" aria-hidden="true">
                <PlusIcon />
              </span>
            </div>
            <div className="item-name-input">
              <ItemInputWithSuggestions
                scenarioId={scenarioId!}
                inputElementRef={addItemInputRef}
                value={newItemName}
                onChange={(value) => {
                  setNewItemName(value);
                  setAddItemError('');
                }}
                onSubmit={addItem}
                placeholder="Add item..."
                excludeItems={items.map(item => item.name)}
              />
            </div>
          </div>
          {addItemError && (
            <p className="add-item-error">{addItemError}</p>
          )}
        </div>
      </div>

    </div>
  )
}

export default List

